// STAB-1A: keep the open assistant conversation across a reload, and say what
// went wrong when a chat request fails. Pure module: no "@/" imports.
// Only a conversation id is stored, never message text.

export const CONVERSATION_KEY = "st_agent_conversation";

/** Shown when a reply stream ended without any text and without an error. */
export const EMPTY_REPLY_CLIENT = "No reply came back. Please try again.";

/** TURN-1: shown when the reply stream dropped and the reloaded conversation has no answer. */
export const DROPPED_REPLY_CLIENT =
  "The connection dropped while I was working. I reloaded the conversation; anything above was kept. "
  + "Please send your message again if it is not answered.";

/** TURN-2: the server is still on this same send. The client reloads once. */
export const TURN_IN_PROGRESS_TEXT =
  "Still working on your last message. I will reload the conversation when it is done.";

/** One id per user send. Letters, digits and dashes, at most 64 characters. */
export function createClientRequestId() {
  const cryptoObj = typeof globalThis.crypto !== "undefined" ? globalThis.crypto : null;
  if (cryptoObj && typeof cryptoObj.randomUUID === "function") {
    try {
      return cryptoObj.randomUUID();
    } catch {
      // non-secure context: fall through to the simple id
    }
  }
  const rand = Math.random().toString(16).slice(2);
  const time = Date.now().toString(16);
  return `r-${time}-${rand}`.replace(/[^A-Za-z0-9-]/g, "").slice(0, 64);
}

function storage() {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage || null;
  } catch {
    return null;
  }
}

/**
 * CHAT-HIST-1: one key per user and page scope.
 * scope is a project id, or "dashboard" when the URL has no project.
 * Calls with no scope keep the old global key (STAB-1A tests, no signed-in user).
 */
export function conversationStorageKey(userId, scope) {
  return `${CONVERSATION_KEY}:${userId}:${scope}`;
}

function storageKey(scopeRef) {
  if (scopeRef && scopeRef.userId && scopeRef.scope) {
    return conversationStorageKey(scopeRef.userId, scopeRef.scope);
  }
  return CONVERSATION_KEY;
}

/** Remember the open conversation id (ignored when empty). */
export function saveConversationId(id, scopeRef) {
  if (!id) return;
  try {
    const s = storage();
    if (s) s.setItem(storageKey(scopeRef), String(id));
  } catch {
    // storage full or blocked: the chat still works without it
  }
}

/** The remembered conversation id, or null. */
export function readConversationId(scopeRef) {
  try {
    const s = storage();
    const value = s ? s.getItem(storageKey(scopeRef)) : null;
    return value || null;
  } catch {
    return null;
  }
}

/** Forget the remembered conversation id for this scope. */
export function clearConversationId(scopeRef) {
  try {
    const s = storage();
    if (s) s.removeItem(storageKey(scopeRef));
  } catch {
    // nothing to do
  }
}

/** Drop the old global key. Its project is unknown, so it is not copied. */
export function removeLegacyConversationKey() {
  try {
    const s = storage();
    if (s) s.removeItem(CONVERSATION_KEY);
  } catch {
    // nothing to do
  }
}

/**
 * CHAT-HIST-1: true when a loaded conversation belongs on this page.
 * scope is a project id, or "dashboard". Older chats have no project_id;
 * those use page_path (no leading slash, id matched exactly).
 */
export function conversationMatchesScope(conversation, scope) {
  const projectId = conversation && conversation.project_id ? String(conversation.project_id) : "";
  const rawPath = conversation && conversation.page_path ? String(conversation.page_path) : "";
  const path = rawPath.replace(/^\/+/, "");
  if (scope === "dashboard") {
    if (projectId) return false;
    return path.indexOf("project/") !== 0;
  }
  if (projectId) return projectId === scope;
  return path === `project/${scope}` || path.indexOf(`project/${scope}/`) === 0;
}

/** Logout: remove the old key and every per-user key. */
export function clearAllConversationKeys() {
  try {
    const s = storage();
    if (!s) return;
    const keys = [];
    for (let i = 0; i < s.length; i += 1) {
      const key = s.key(i);
      if (key === CONVERSATION_KEY || (key && key.startsWith(`${CONVERSATION_KEY}:`))) {
        keys.push(key);
      }
    }
    keys.forEach((key) => s.removeItem(key));
  } catch {
    // storage blocked: logout still clears the session
  }
}

/** Plain-language text for a failed chat request, by HTTP status. */
export function chatErrorMessage(status) {
  if (status === 429) return "You've hit the assistant's limit for now. Please wait a minute and try again.";
  if (status === 401) return "Your session has expired. Please sign in again.";
  return "I'm having trouble connecting. Please try again.";
}
